import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "./components/Layout";
import PageAnimation from "./components/PageAnimation";
import { PageLoader } from "./components/LoadingFallback";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { useApp } from "./context/AppContext";
import { getProfile, logout, supersededByRemoteLogin } from "./store/slices/authSlice";
import {
  syncTabAuthSessionWithStorage,
  isAuthSuperseded,
  subscribeRemoteAuthStorage,
} from "./utils/authSession";
import { toastError } from "./utils/toast";

const LoginView = lazy(() => import("./modules/auth/LoginView"));
const DashboardView = lazy(() => import("./modules/dashboard/DashboardView"));
const VisualizerView = lazy(() => import("./modules/visualizer/VisualizerView"));
const RoomCanvasView = lazy(() => import("./modules/visualizer/RoomCanvasViewCommercial"));
const PanoramaRoomView = lazy(() => import("./components/panaroma/PanoramaRoomView"));
const AdminView = lazy(() => import("./modules/admin/AdminView"));
const SettingsView = lazy(() => import("./modules/settings/SettingsView"));
const HomeView = lazy(() => import("./modules/marketing/HomeView"));
const FeaturesView = lazy(() => import("./modules/marketing/FeaturesView"));
const DemoView = lazy(() => import("./modules/marketing/DemoView"));
const ContactView = lazy(() => import("./modules/marketing/ContactView"));

function App() {
  const dispatch = useAppDispatch();
  const { user: contextUser, isGuestSessionValid } = useApp();
  const { user, token, isAuthenticated: reduxAuth } = useAppSelector((state) => ({
    user: state.auth.user,
    token: state.auth.token,
    isAuthenticated: state.auth.isAuthenticated,
  }));

  // Token-based: allow guest (no token) or logged-in user
  const isGuest = contextUser?.role === "guest" && Boolean(isGuestSessionValid?.());
  const isAuthenticated = Boolean(reduxAuth || isGuest);

  // Rehydrate profile: when token exists, fetch profile to keep state fresh without blocking or dropping auth
  useEffect(() => {
    if (token && !isAuthSuperseded()) {
      dispatch(getProfile());
    }
  }, [token, dispatch]);

  // Other tab signed in → new authSessionId; or shared logout cleared token
  useEffect(() => {
    const onRemoteAuthChange = () => {
      const lsToken = localStorage.getItem("token");
      if (!lsToken) {
        dispatch(logout());
        window.location.href = "/login";
        return;
      }
      if (syncTabAuthSessionWithStorage()) {
        dispatch(supersededByRemoteLogin());
        // Avoid showing this toast when the user pressed Logout in another tab.
        try {
          const stamp = Number(localStorage.getItem('tv_manual_logout_at') || '0');
          const within = stamp && Date.now() - stamp < 60000;
          if (within) localStorage.removeItem('tv_manual_logout_at');
          if (!within) {
            toastError(
              "You were signed out because this account signed in on another tab or device."
            );
          }
        } catch (_) {
          toastError("You were signed out because this account signed in on another tab or device.");
        }
        window.location.href = "/login";
      }
    };
    return subscribeRemoteAuthStorage(onRemoteAuthChange);
  }, [dispatch]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<PageAnimation><HomeView /></PageAnimation>} />
        <Route path="/panorama" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
        <Route path="/3d" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
        <Route path="/360" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
        <Route path="/features" element={<PageAnimation><FeaturesView /></PageAnimation>} />
        <Route path="/demo" element={<PageAnimation><DemoView /></PageAnimation>} />
        <Route path="/contact" element={<PageAnimation><ContactView /></PageAnimation>} />
        <Route path="/login" element={<PageAnimation><LoginView /></PageAnimation>} />

        <Route element={isAuthenticated ? <Layout /> : <Navigate to="/login" replace />}>
          <Route path="/dashboard" element={<PageAnimation><DashboardView /></PageAnimation>} />
          <Route path="/visualizer" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
          <Route path="/panorama" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
          <Route path="/3d" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
          <Route path="/360" element={<PageAnimation><PanoramaRoomView /></PageAnimation>} />
          <Route path="/room-canvas" element={<PageAnimation><RoomCanvasView /></PageAnimation>} />
          <Route
            path="/admin"
            element={
              ["superAdmin", "admin"].includes(user?.role) ? (
                <PageAnimation><AdminView /></PageAnimation>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              ["superAdmin", "admin"].includes(user?.role) ? (
                <PageAnimation><SettingsView /></PageAnimation>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
